import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { illustrativeRubricNote } from '../../data/developmentDomains';
import type { DevSectionId } from '../../types/developmentDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { glassPanel, primaryButton } from '../../utils/demoUi';
import { decisionLabels, deriveStage, participantName, stageLabels } from '../../utils/developmentDemo';
import { CycleStrip } from './CycleStrip';
import { StageBadge } from './DevBadges';
const nextSection: Record<ReturnType<typeof deriveStage>, {
  section: DevSectionId;
  label: string;
} | null> = {
  'ready-initial': {
    section: 'task',
    label: 'Open my task'
  },
  'awaiting-review': {
    section: 'task',
    label: 'View my submission'
  },
  revision: {
    section: 'task',
    label: 'Revise my response'
  },
  'awaiting-activity': {
    section: 'task',
    label: 'Read my feedback'
  },
  practising: {
    section: 'plan',
    label: 'Open development plan'
  },
  'ready-reassessment': {
    section: 'task',
    label: 'Take the follow-up scenario'
  },
  'cycle-complete': {
    section: 'progress',
    label: 'Compare my rounds'
  }
};
export function DevParticipantOverview() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const participantId = state.ui.actingParticipantId;
  const participant = config.participants.find(item => item.id === participantId);
  const stage = deriveStage(state, participantId);
  const next = nextSection[stage];
  const decisions = state.decisions.filter(item => item.participantId === participantId);
  const latestDecision = decisions[decisions.length - 1];
  const referrals = state.referrals.filter(item => item.participantId === participantId);
  return <div data-id="element-636" data-mp="23xgegee6nsvy">
      <p className="text-sm font-medium text-lavender" data-id="element-637" data-mp="24146f93zc78k">{config.organization}</p>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-ink-dark lg:text-4xl" data-id="element-638" data-mp="ogpnksw2htmu">
        Welcome, {participantName(config, participantId).split(' ')[0]}
      </h1>
      <p className="mt-2 text-ink-muted" data-id="element-639" data-mp="2v5hvzsubobvw">{participant?.description}</p>

      <section aria-labelledby="next-step" className={`${glassPanel} mt-8 p-6 lg:p-8`} data-id="element-640" data-mp="2q6wu7ozf9k5c">
        <div className="flex flex-wrap items-center gap-3" data-id="element-641" data-mp="2lggh65zik0ji">
          <h2 id="next-step" className="font-heading text-lg font-bold text-ink-dark" data-id="element-642" data-mp="2lex0wfduxphq">
            Your next step
          </h2>
          <StageBadge stage={stage} data-id="element-643" data-mp="2cg1t4yv607cb" />
        </div>
        <p className="mt-2 text-lg leading-relaxed text-ink-dark" data-id="element-644" data-mp="2cgtcnbbhmem8">{stageLabels[stage].participant}</p>
        {next && <button type="button" onClick={() => dispatch({
        type: 'setSection',
        section: next.section
      })} className={`mt-5 ${primaryButton}`} data-id="element-645" data-mp="27fnn1md4j7gx">
            {next.label}
            <ArrowRight size={16} aria-hidden="true" data-id="element-646" data-mp="1xjua41vhtbym" />
          </button>}
        <div className="mt-8" data-id="element-647" data-mp="r0rjec16hvg">
          <CycleStrip stage={stage} data-id="element-648" data-mp="pz7ikd1el1l" />
        </div>
      </section>

      {config.features.comparison && <section aria-labelledby="stage-decision" className="mt-6 rounded-2xl border border-line bg-white/80 p-6" data-id="element-649" data-mp="3rxlgmsiizfpu">
          <h2 id="stage-decision" className="font-heading text-lg font-bold text-ink-dark" data-id="element-650" data-mp="46c1swv7p5rg">
            Hiring stage decision
          </h2>
          <p className="mt-1 text-sm text-ink-muted" data-id="element-651" data-mp="d0gqb9mvi53t">Recorded by a human reviewer, separately from your development feedback.</p>
          {latestDecision ? <p className="mt-3 leading-relaxed text-ink-dark" data-id="element-652" data-mp="i1nz9c0r5ac2">
              <span className="font-semibold" data-id="element-653" data-mp="3hg9r80idczc8">{decisionLabels[latestDecision.outcome]}</span> · {formatTimestamp(latestDecision.at)}
              <span className="mt-1 block text-sm text-ink-muted" data-id="element-654" data-mp="3lid7d2fzgs3u">Reason: {latestDecision.reason}</span>
            </p> : <p className="mt-3 text-ink-dark" data-id="element-655" data-mp="3vf98gn3jybyd">No decision recorded yet.</p>}
        </section>}

      {config.features.referrals && <section aria-labelledby="referral-summary" className="mt-6 rounded-2xl border border-line bg-white/80 p-6" data-id="element-656" data-mp="15p5rt9ruccg3">
          <h2 id="referral-summary" className="font-heading text-lg font-bold text-ink-dark" data-id="element-657" data-mp="1aqbqdo86mtxj">
            Referrals made with your agreement
          </h2>
          <p className="mt-2 text-ink-dark" data-id="element-658" data-mp="2enns6othi1ap">
            {referrals.length === 0 ? 'No referrals recorded.' : `${referrals.length} referral${referrals.length === 1 ? '' : 's'} recorded.`}
          </p>
          <button type="button" onClick={() => dispatch({
        type: 'setSection',
        section: 'my-referrals'
      })} className="mt-2 text-sm font-semibold text-navy hover:text-navy-light" data-id="element-659" data-mp="2nm9aciv1neay">
            View My Referrals
          </button>
        </section>}

      <p className="mt-8 text-sm leading-relaxed text-ink-muted" data-id="element-660" data-mp="3bcdbmjkrf1ys">
        {config.note} {illustrativeRubricNote}
      </p>
    </div>;
}